// The supply projection in the docs: a few assumptions to set, the supply drawn over ten years with and
// without the burn auction, and the numbers year by year. The model is in projection.ts.
import { useMemo, useState, type ChangeEvent } from 'react';

import { config } from '../config.ts';
import { useApp } from '../state.tsx';
import { breakEvenPrice, fmtCount, fmtPrice, project, type Regime } from './projection.ts';

/** The live soft-launch rate (1 until the app has read the chain), or 0 once the curve has taken over */
function useFixedRate(): number {
  const { globals } = useApp();
  return globals ? Number(globals.mint.fixedRate) / 1e18 : 1;
}

/** The break-even table's row for the soft launch, at the live rate; nothing once the curve has taken over */
export function SoftLaunchBreakEven() {
  const rate = useFixedRate();
  if (rate <= 0) return null;
  return (
    <tr>
      <td>{rate.toLocaleString('en-US')}</td>
      <td>the soft launch (the live rate)</td>
      <td>{fmtPrice(breakEvenPrice(rate, config.auctionPct / 100, config.stakerLossPct / 100))}</td>
    </tr>
  );
}

const YEARS = 10;
const TABLE_YEARS = [1, 2, 3, 5, 10];
const CHART_W = 640;
const CHART_H = 220;
const PAD = { top: 10, right: 12, bottom: 24, left: 44 };

export function BurnProjection() {
  const liveFixed = useFixedRate();
  const fixedRate = liveFixed > 0 ? liveFixed : 1;
  const [losses, setLosses] = useState(1_000_000);
  const [yieldPct, setYieldPct] = useState(50);
  const [stakedPct, setStakedPct] = useState(80);
  // The curve by default, whatever the live rate: the long run is what the page is about, the soft launch is a footnote
  const [regime, setRegime] = useState<Regime>('curve');

  const rows = useMemo(
    () =>
      project({
        lossesPerMonth: losses,
        yieldPct,
        stakedPct,
        regime,
        fixedRate,
        flatRate: 100,
        tailKUsd: config.mintTailKM * 1e6,
        auctionShare: config.auctionPct / 100,
        stakerLossShare: config.stakerLossPct / 100,
        years: YEARS,
      }),
    [losses, yieldPct, stakedPct, regime, fixedRate],
  );
  const last = rows[rows.length - 1]!;
  const top = Math.max(last.minted, 1);
  const x = (year: number) => PAD.left + (year / YEARS) * (CHART_W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - v / top) * (CHART_H - PAD.top - PAD.bottom);
  const path = (pick: (r: (typeof rows)[number]) => number) => rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(r.year).toFixed(1)},${y(pick(r)).toFixed(1)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  const burnedShare = last.minted > 0 ? last.burned / last.minted : 0;

  const num = (set: (v: number) => void, min: number, max: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value.replace(/[^0-9.]/g, ''));
    if (Number.isFinite(v)) set(Math.min(max, Math.max(min, v)));
  };

  return (
    <div className="doc-calc">
      <div className="doc-calc-inputs">
        <label>
          Net losses per month
          <span>
            $<input inputMode="numeric" value={losses.toLocaleString('en-US')} onChange={num(setLosses, 0, 1e9)} />
          </span>
        </label>
        <label>
          Yield holders demand
          <span>
            <input inputMode="decimal" value={yieldPct} onChange={num(setYieldPct, 0, 1000)} />% a year
          </span>
        </label>
        <label>
          Share of supply staked
          <span>
            <input inputMode="decimal" value={stakedPct} onChange={num(setStakedPct, 0, 100)} />%
          </span>
        </label>
        <label>
          Mint rate
          <select value={regime} onChange={(e) => setRegime(e.target.value as Regime)}>
            <option value="curve">The curve: 100 per $1, decaying</option>
            <option value="fixed">Soft launch: {fixedRate.toLocaleString('en-US')} per $1 lost</option>
          </select>
        </label>
      </div>

      <svg className="doc-chart" viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label="Supply over ten years, with and without the burn auction">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(t)} y2={y(t)} className="doc-chart-grid" />
            <text x={PAD.left - 6} y={y(t) + 3} textAnchor="end" className="doc-chart-label">
              {fmtCount(t)}
            </text>
          </g>
        ))}
        {rows.map((r) => (
          <text key={r.year} x={x(r.year)} y={CHART_H - 6} textAnchor="middle" className="doc-chart-label">
            {r.year === 0 ? 'now' : `${r.year}y`}
          </text>
        ))}
        <path d={path((r) => r.minted)} className="doc-chart-minted" />
        <path d={path((r) => r.supply)} className="doc-chart-supply" />
      </svg>
      <div className="doc-chart-legend">
        <span>
          <i className="minted" /> Minted, nothing burned
        </span>
        <span>
          <i className="supply" /> Supply with the auction
        </span>
        <span className="doc-chart-sum">
          After {YEARS} years: {fmtCount(last.burned)} of {fmtCount(last.minted)} PAPURR burned ({Math.round(burnedShare * 100)}%)
        </span>
      </div>

      <table className="doc-table">
        <thead>
          <tr>
            <th>Year</th>
            <th>Minted</th>
            <th>Burned</th>
            <th>Supply</th>
            <th>Burned / minted</th>
            <th>Implied price</th>
          </tr>
        </thead>
        <tbody>
          {TABLE_YEARS.map((yr) => {
            const r = rows[yr]!;
            return (
              <tr key={yr}>
                <td>{yr}</td>
                <td>{fmtCount(r.minted)}</td>
                <td>{fmtCount(r.burned)}</td>
                <td>{fmtCount(r.supply)}</td>
                <td>{r.minted > 0 ? `${Math.round((r.burned / r.minted) * 100)}%` : '—'}</td>
                <td>{fmtPrice(r.price)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
